import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

const PreCompetitionClasses = ({ isOpen, onClose }) => {
  const [bioExpanded, setBioExpanded] = useState({ jacqueline: false, riho: false });
  const [selectedKeys, setSelectedKeys] = useState([]); // array of "instructorKey::date" strings
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', notes: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [confirmedItems, setConfirmedItems] = useState([]); // snapshot of selected classes at time of submit
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent background page scroll while modal is open (iOS + Android)
  useEffect(() => {
    if (isOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prevOverflow; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const CLASS_YEAR = 2026;

  const sessions = [
    {
      key: 'jacqueline',
      instructor: 'Jacqueline Zhang',
      dayLabel: 'Sundays',
      time: '11:00 AM – 1:00 PM',
      startTime: [11, 0], // 11:00 AM
      endTime: [13, 0],   // 1:00 PM
      dates: [
        { display: 'August 23', month: 8, day: 23 },
        { display: 'August 30', month: 8, day: 30 },
        { display: 'September 6', month: 9, day: 6 }
      ],
      shortBio: 'Canadian professional dancer with the Tivoli Ballet Theatre (Copenhagen), the National Ballet of Canada, and Ballet Jörgen. RAD Solo Seal Award recipient, ARAD, Registered RAD Teacher, and Level 3 PBT instructor.',
      fullBio: `Jacqueline Zhang is a Canadian professional dancer who has performed with the Tivoli Ballet Theatre in Copenhagen, Denmark. During her time with the company, she danced the lead principal role in The Nutcracker and performed prominent corps de ballet roles in The Snow Queen. She has also appeared in the National Ballet of Canada's production of The Nutcracker and danced with Ballet Jörgen's Junior Company, where she was part of the creation of A Midsummer Night's Dream.

Jacqueline has received numerous scholarships and awards throughout her career, including Top 12 in Classical Ballet and 3rd Place in Contemporary at the Youth America Grand Prix (YAGP). Her success in the competition circuit earned her a feature on the cover of North York Post Magazine as part of their "20 Under 20 – The Most Dynamic and Innovative Young People in Toronto" issue in 2019. She holds the prestigious RAD Solo Seal Award and has been granted the title of Associate of the Royal Academy of Dance (ARAD) after earning Distinction in both the RAD Advanced 1 and Advanced 2 examinations.

Beyond her performance career, Jacqueline is an experienced and dedicated dance educator. She is a Registered Teacher with the Royal Academy of Dance, a Level 3 certified Progressing Ballet Technique (PBT) instructor, and a certified Pilates instructor. She holds a Bachelor of Fine Arts in Dance Education from York University. Jacqueline is passionate about bringing her international performance experience to her students and supporting them through high-level, anatomically informed, and sustainable training.`
    },
    {
      key: 'riho',
      instructor: 'Riho Ogata',
      dayLabel: 'Thursdays',
      time: '6:30 – 8:30 PM',
      startTime: [18, 30], // 6:30 PM
      endTime: [20, 30],   // 8:30 PM
      dates: [
        { display: 'August 27', month: 8, day: 27 },
        { display: 'September 3', month: 9, day: 3 },
        { display: 'September 10', month: 9, day: 10 },
        { display: 'September 17', month: 9, day: 17 }
      ],
      shortBio: 'Toronto-based ballet artist and educator. Founder of Ogata Ballet School, faculty at The Fifth Dance, and a founding member of Prix du Canada.',
      fullBio: `Originally from Kyoto, Japan, Riho began ballet at age three and trained extensively in Kyoto, performing lead roles in productions such as The Sleeping Beauty, The Nutcracker, and Swan Lake. In 2015, she received the Uiro Prize at the National Ballet Competition in Nagoya before continuing her training in New York City from 2016 to 2017.

After moving to Toronto in 2018, she performed principal roles with Toronto International Ballet Theatre, including The Nutcracker. Riho is the founder of Ogata Ballet School, a faculty member at The Fifth Dance, and a founding member of Prix du Canada. She holds a Bachelor of Laws from Ritsumeikan University and is certified in Progressing Ballet Technique (PBT).`
    }
  ];

  const toggleDate = (instructorKey, date) => {
    const key = `${instructorKey}::${date}`;
    setSelectedKeys((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Convert selectedKeys → structured items with full session + date info (for calendar generation)
  const buildConfirmedItems = () => {
    return selectedKeys.map((k) => {
      const [instructorKey, dateDisplay] = k.split('::');
      const session = sessions.find((s) => s.key === instructorKey);
      const dateObj = session ? session.dates.find((d) => d.display === dateDisplay) : null;
      return session && dateObj ? { session, dateObj } : null;
    }).filter(Boolean);
  };

  // Format a local date/time for calendar strings — YYYYMMDDTHHMMSS (no zone marker)
  const pad2 = (n) => String(n).padStart(2, '0');
  const formatLocalDateTime = (year, month, day, hour, minute) =>
    `${year}${pad2(month)}${pad2(day)}T${pad2(hour)}${pad2(minute)}00`;

  // Build a Google Calendar "Add Event" URL for one class occurrence
  const buildGoogleCalendarUrl = ({ session, dateObj }) => {
    const [sh, sm] = session.startTime;
    const [eh, em] = session.endTime;
    const start = formatLocalDateTime(CLASS_YEAR, dateObj.month, dateObj.day, sh, sm);
    const end = formatLocalDateTime(CLASS_YEAR, dateObj.month, dateObj.day, eh, em);
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `Pre-Competition Practice with ${session.instructor}`,
      dates: `${start}/${end}`,
      ctz: 'America/Toronto',
      details: `Prix du Canada Pre-Competition Practice Class — Barre + Variation with ${session.instructor}.\n\n$35 CAD + HST per class. Payment instructions will be sent in your confirmation email.\n\nhttps://www.prixducanada.com/learn-explore?preclasses=open`,
      location: 'Winchester Street Theatre, Toronto'
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Build .ics file content containing all confirmed classes
  const buildIcsContent = (items) => {
    const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const events = items.map(({ session, dateObj }) => {
      const [sh, sm] = session.startTime;
      const [eh, em] = session.endTime;
      const start = formatLocalDateTime(CLASS_YEAR, dateObj.month, dateObj.day, sh, sm);
      const end = formatLocalDateTime(CLASS_YEAR, dateObj.month, dateObj.day, eh, em);
      const uid = `pdc-preclass-${CLASS_YEAR}${pad2(dateObj.month)}${pad2(dateObj.day)}-${session.key}@prixducanada.com`;
      return [
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${now}`,
        `DTSTART;TZID=America/Toronto:${start}`,
        `DTEND;TZID=America/Toronto:${end}`,
        `SUMMARY:Pre-Competition Practice with ${session.instructor}`,
        `DESCRIPTION:Prix du Canada Pre-Competition Practice Class — Barre + Variation with ${session.instructor}. $35 CAD + HST per class. Payment instructions sent in confirmation email.`,
        'LOCATION:Winchester Street Theatre\\, Toronto',
        'ORGANIZER;CN=Prix du Canada:mailto:info@prixducanada.com',
        'END:VEVENT'
      ].join('\r\n');
    }).join('\r\n');

    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Prix du Canada//Pre-Competition Classes//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      // VTIMEZONE definition so calendar apps interpret DTSTART correctly
      'BEGIN:VTIMEZONE',
      'TZID:America/Toronto',
      'BEGIN:STANDARD',
      'DTSTART:19701101T020000',
      'TZOFFSETFROM:-0400',
      'TZOFFSETTO:-0500',
      'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU',
      'TZNAME:EST',
      'END:STANDARD',
      'BEGIN:DAYLIGHT',
      'DTSTART:19700308T020000',
      'TZOFFSETFROM:-0500',
      'TZOFFSETTO:-0400',
      'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU',
      'TZNAME:EDT',
      'END:DAYLIGHT',
      'END:VTIMEZONE',
      events,
      'END:VCALENDAR'
    ].join('\r\n');
  };

  const downloadIcs = () => {
    const content = buildIcsContent(confirmedItems);
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prix-du-canada-pre-competition-classes.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const numberOfClasses = selectedKeys.length;
  const totalCost = numberOfClasses * 35;

  const selectionSummary = sessions.map((s) => {
    const picked = selectedKeys
      .filter((k) => k.startsWith(`${s.key}::`))
      .map((k) => k.split('::')[1]);
    return picked.length > 0
      ? `${s.instructor} (${s.dayLabel}, ${s.time}): ${picked.join(', ')}`
      : null;
  }).filter(Boolean).join('\n');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedKeys.length === 0) {
      setSubmitStatus('needs-dates');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('');

    const payload = {
      ...formData,
      selectedClasses: selectionSummary,
      numberOfClasses,
      pricePerClass: '$35 CAD + HST',
      totalCost: `$${totalCost} CAD + HST`,
      location: 'Winchester Street Theatre',
      submissionType: 'Pre-Competition Class Registration'
    };

    try {
      const response = await fetch('https://formspree.io/f/manjkjaa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        // Snapshot the selection BEFORE clearing so the success screen can build calendar links
        setConfirmedItems(buildConfirmedItems());
        setSubmitStatus('submitted');
        setFormData({ name: '', email: '', phone: '', notes: '' });
        setSelectedKeys([]);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
    setIsSubmitting(false);
  };

  // Wrap onClose so we reset the success/calendar state after the modal closes
  const handleClose = () => {
    onClose();
    // Give the exit animation a beat before resetting
    setTimeout(() => {
      setSubmitStatus('');
      setConfirmedItems([]);
    }, 200);
  };

  // Common styles
  const inputStyle = {
    width: '100%',
    padding: isMobile ? '0.95rem 1rem' : '0.75rem 1rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '16px', // 16px prevents iOS zoom-on-focus
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    WebkitAppearance: 'none',
    appearance: 'none'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.4rem',
    fontWeight: '600',
    fontSize: '0.9rem',
    color: 'var(--charcoal)',
    textAlign: 'left'
  };

  const eyebrowStyle = {
    fontSize: '0.7rem',
    color: 'var(--primary-gold)',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    fontWeight: '600',
    margin: '0 0 0.25rem 0'
  };

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.8)', zIndex: 9999,
      display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
      padding: isMobile ? '0' : '20px',
      WebkitOverflowScrolling: 'touch'
    }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: isMobile ? '16px 16px 0 0' : '15px',
        maxWidth: '760px', width: '100%',
        maxHeight: isMobile ? '95dvh' : '92vh',
        height: isMobile ? '95dvh' : 'auto',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: isMobile ? 'env(safe-area-inset-top)' : 0,
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0
      }}>

        {/* Sticky mobile handle bar */}
        {isMobile && (
          <div style={{
            display: 'flex', justifyContent: 'center', padding: '8px 0 4px', flexShrink: 0
          }}>
            <div style={{ width: '40px', height: '4px', background: '#ddd', borderRadius: '2px' }} />
          </div>
        )}

        {/* Sticky Close Button (always visible) */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: isMobile ? 'calc(env(safe-area-inset-top) + 10px)' : '14px',
            right: isMobile ? '10px' : '14px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            fontSize: '22px', cursor: 'pointer',
            color: 'var(--charcoal)', zIndex: 20,
            width: '44px', height: '44px',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1, padding: 0,
            WebkitTapHighlightColor: 'transparent',
            transition: 'transform 0.15s ease'
          }}
          onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.92)'; }}
          onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          aria-label="Close"
        >
          ×
        </button>

        {/* Scrollable Content */}
        <div style={{
          overflowY: 'auto', overflowX: 'hidden', flex: 1,
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain'
        }}>
          {/* Header */}
          <div style={{
            padding: isMobile ? '1.5rem 1.25rem 1.25rem' : '2.5rem 2.5rem 1.5rem',
            textAlign: 'center', borderBottom: '1px solid #eee'
          }}>
            <p style={{ margin: '0 0 0.5rem 0', color: 'var(--primary-gold)', fontSize: '0.7rem', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: '600' }}>
              Prix du Canada
            </p>
            <h2 style={{
              fontSize: isMobile ? '1.5rem' : '2rem', color: 'var(--charcoal)',
              margin: '0 0 0.75rem 0', lineHeight: '1.2',
              fontFamily: "'Playfair Display', Georgia, serif",
              padding: isMobile ? '0 1.5rem' : 0
            }}>
              Pre-Competition Practice Classes
            </h2>
            <p style={{
              fontSize: isMobile ? '0.95rem' : '1rem', color: 'var(--soft-gray)',
              lineHeight: '1.6', margin: '0 auto', maxWidth: '580px'
            }}>
              Refine your technique, polish your variation, and step onto the Prix du Canada stage with confidence. Designed for dancers preparing for the Adult Recreational Division.
            </p>
          </div>

          {/* Body */}
          <div style={{ padding: isMobile ? '1.25rem' : '2.5rem' }}>

            {/* Shared Info Block */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: isMobile ? '0.75rem' : '1rem',
              marginBottom: isMobile ? '1.5rem' : '1.75rem',
              padding: isMobile ? '1rem 1.1rem' : '1.25rem',
              background: 'var(--soft-cream)',
              borderRadius: '10px',
              borderLeft: '4px solid var(--primary-gold)'
            }}>
              <div>
                <p style={eyebrowStyle}>Location</p>
                <p style={{ margin: 0, color: 'var(--charcoal)', fontSize: '0.95rem', lineHeight: '1.4' }}>
                  Winchester Street Theatre
                </p>
              </div>
              <div>
                <p style={eyebrowStyle}>Price</p>
                <p style={{ margin: 0, color: 'var(--charcoal)', fontSize: '0.95rem', lineHeight: '1.4' }}>
                  <strong>$35 CAD</strong> + HST / class
                </p>
              </div>
              <div>
                <p style={eyebrowStyle}>Format</p>
                <p style={{ margin: 0, color: 'var(--charcoal)', fontSize: '0.95rem', lineHeight: '1.4' }}>
                  2 hours · barre + variation
                </p>
              </div>
            </div>

            {/* Date Selection */}
            <div style={{ marginBottom: isMobile ? '1.25rem' : '1.75rem' }}>
              <p style={{ margin: '0 0 1rem 0', fontWeight: '700', color: 'var(--charcoal)', fontSize: isMobile ? '1.05rem' : '1rem', textAlign: 'left' }}>
                Select the dates you'd like to attend <span style={{ color: 'var(--primary-gold)' }}>*</span>
              </p>

              {sessions.map((s, idx) => (
                <div key={s.key} style={{ marginBottom: idx === sessions.length - 1 ? 0 : (isMobile ? '1.5rem' : '1.25rem') }}>
                  <div style={{
                    display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap',
                    marginBottom: '0.65rem'
                  }}>
                    <span style={{ color: 'var(--charcoal)', fontSize: isMobile ? '1rem' : '0.95rem', fontWeight: '700' }}>
                      {s.dayLabel} with {s.instructor}
                    </span>
                    <span style={{ color: 'var(--soft-gray)', fontSize: isMobile ? '0.9rem' : '0.85rem' }}>
                      · {s.time}
                    </span>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '0.55rem'
                  }}>
                    {s.dates.map((date) => {
                      const key = `${s.key}::${date.display}`;
                      const isSelected = selectedKeys.includes(key);
                      return (
                        <button
                          type="button"
                          key={date.display}
                          onClick={() => toggleDate(s.key, date.display)}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: isMobile ? '0.95rem 0.6rem' : '0.7rem 0.6rem',
                            minHeight: isMobile ? '52px' : '44px',
                            borderRadius: '10px',
                            border: `2px solid ${isSelected ? 'var(--primary-gold)' : '#ddd'}`,
                            background: isSelected ? 'var(--soft-cream)' : 'white',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease, transform 0.1s ease',
                            fontSize: isMobile ? '0.95rem' : '0.9rem',
                            fontWeight: isSelected ? '700' : '500',
                            color: isSelected ? 'var(--primary-gold)' : 'var(--charcoal)',
                            textAlign: 'center', userSelect: 'none',
                            fontFamily: 'inherit',
                            WebkitTapHighlightColor: 'transparent',
                            boxShadow: isSelected ? '0 2px 8px rgba(201, 169, 110, 0.25)' : 'none'
                          }}
                          onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
                          onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                          {isSelected && <span style={{ marginRight: '0.4rem', fontSize: '0.9em' }}>✓</span>}
                          {date.display}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {numberOfClasses > 0 && (
                <div style={{
                  marginTop: '1rem',
                  padding: isMobile ? '0.9rem 1rem' : '0.75rem 1rem',
                  background: 'linear-gradient(135deg, #faf8f5 0%, #f4ebd9 100%)',
                  border: '1px solid var(--primary-gold)', borderRadius: '10px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  flexWrap: 'wrap', gap: '0.5rem'
                }}>
                  <span style={{ fontSize: isMobile ? '0.95rem' : '0.9rem', color: 'var(--charcoal)', fontWeight: '600' }}>
                    {numberOfClasses} class{numberOfClasses > 1 ? 'es' : ''} selected
                  </span>
                  <strong style={{ fontSize: isMobile ? '1.15rem' : '1.05rem', color: 'var(--deep-gold)' }}>
                    ${totalCost} CAD + HST
                  </strong>
                </div>
              )}
            </div>

            {/* Signup Form / Success */}
            {submitStatus === 'submitted' ? (
              <div style={{ marginBottom: '1.5rem' }}>
                {/* Success confirmation */}
                <div style={{
                  padding: isMobile ? '1.75rem 1.25rem' : '2rem', background: '#d4edda', border: '1px solid #c3e6cb',
                  borderRadius: '12px', textAlign: 'center', marginBottom: '1.25rem'
                }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✓</div>
                  <h3 style={{ margin: '0 0 0.75rem 0', color: '#155724', fontSize: '1.25rem' }}>Registration Received</h3>
                  <p style={{ margin: 0, color: '#155724', lineHeight: '1.6', fontSize: '0.95rem' }}>
                    Thank you! We've received your registration. Within 24 hours, we'll send you a confirmation email with payment instructions to secure your spot.
                  </p>
                </div>

                {/* Add to Calendar */}
                {confirmedItems.length > 0 && (
                  <div style={{
                    padding: isMobile ? '1.25rem 1.1rem' : '1.5rem',
                    background: 'var(--soft-cream)',
                    border: '1px solid var(--primary-gold)',
                    borderRadius: '12px'
                  }}>
                    <p style={{ ...eyebrowStyle, textAlign: 'left', marginBottom: '0.5rem' }}>
                      Add to Your Calendar
                    </p>
                    <h4 style={{ margin: '0 0 1rem 0', color: 'var(--charcoal)', fontSize: '1.1rem', fontFamily: "'Playfair Display', Georgia, serif", textAlign: 'left' }}>
                      Save your {confirmedItems.length === 1 ? 'class' : 'classes'} so you don't miss a beat
                    </h4>

                    {/* Primary CTA: download .ics */}
                    <button
                      type="button"
                      onClick={downloadIcs}
                      style={{
                        width: '100%',
                        padding: isMobile ? '0.95rem 1rem' : '0.85rem 1rem',
                        minHeight: isMobile ? '50px' : 'auto',
                        background: 'white',
                        color: 'var(--charcoal)',
                        border: '2px solid var(--primary-gold)',
                        borderRadius: '10px',
                        fontSize: isMobile ? '1rem' : '0.95rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        WebkitTapHighlightColor: 'transparent',
                        transition: 'transform 0.1s ease, background 0.15s ease',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
                      }}
                      onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                      onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      <span aria-hidden="true">📅</span>
                      {confirmedItems.length === 1 ? 'Save this class to my calendar' : `Save all ${confirmedItems.length} classes to my calendar`}
                    </button>
                    <p style={{ margin: '0.5rem 0 1.25rem 0', fontSize: '0.8rem', color: 'var(--soft-gray)', textAlign: 'center', lineHeight: '1.55' }}>
                      Downloads a small calendar file that opens in your calendar app (Apple Calendar, Google Calendar on Android, Outlook) — tap it to add all classes at once.<br />
                      On desktop using Google Calendar? Use the one-tap links below instead.
                    </p>

                    {/* Divider */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0 1rem' }}>
                      <div style={{ flex: 1, height: '1px', background: 'rgba(0,0,0,0.08)' }} />
                      <span style={{ fontSize: '0.75rem', color: 'var(--soft-gray)', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>Or</span>
                      <div style={{ flex: 1, height: '1px', background: 'rgba(0,0,0,0.08)' }} />
                    </div>

                    {/* Per-class Google Calendar links */}
                    <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.85rem', color: 'var(--charcoal)', fontWeight: '600', textAlign: 'left' }}>
                      Add each class to Google Calendar:
                    </p>
                    <div style={{ display: 'grid', gap: '0.5rem' }}>
                      {confirmedItems.map(({ session, dateObj }, idx) => (
                        <a
                          key={idx}
                          href={buildGoogleCalendarUrl({ session, dateObj })}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: isMobile ? '0.85rem 1rem' : '0.7rem 1rem',
                            minHeight: isMobile ? '48px' : 'auto',
                            background: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            color: 'var(--charcoal)',
                            textDecoration: 'none',
                            fontSize: '0.9rem',
                            fontFamily: 'inherit',
                            WebkitTapHighlightColor: 'transparent',
                            transition: 'border-color 0.15s ease, transform 0.1s ease'
                          }}
                          onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                          onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                          <span style={{ textAlign: 'left' }}>
                            <strong>{dateObj.display}</strong>
                            <span style={{ color: 'var(--soft-gray)' }}> · with {session.instructor.split(' ')[0]}</span>
                          </span>
                          <span style={{ color: 'var(--primary-gold)', fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap', marginLeft: '0.75rem' }}>
                            Add →
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Name <span style={{ color: 'var(--primary-gold)' }}>*</span></label>
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange} required
                      autoComplete="name"
                      style={inputStyle} placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Email <span style={{ color: 'var(--primary-gold)' }}>*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} required
                      autoComplete="email" inputMode="email"
                      style={inputStyle} placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Phone <span style={{ fontWeight: '400', color: 'var(--soft-gray)' }}>(optional)</span></label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                    autoComplete="tel" inputMode="tel"
                    style={inputStyle} placeholder="+1 (416) 000-0000"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Notes <span style={{ fontWeight: '400', color: 'var(--soft-gray)' }}>(optional)</span></label>
                  <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={isMobile ? 3 : 3}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '90px' }}
                    placeholder="Which variation are you preparing? Any experience or questions to share?"
                  />
                </div>

                {submitStatus === 'needs-dates' && (
                  <div style={{
                    padding: '0.85rem 1rem', background: '#fff3cd', border: '1px solid #ffe69c',
                    borderRadius: '8px', color: '#7a5a00', fontSize: '0.9rem'
                  }}>
                    Please select at least one date above.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div style={{
                    padding: '0.85rem 1rem', background: '#f8d7da', border: '1px solid #f5c6cb',
                    borderRadius: '8px', color: '#721c24', fontSize: '0.9rem'
                  }}>
                    Something went wrong. Please try again or email <a href="mailto:info@prixducanada.com" style={{ color: '#721c24', fontWeight: '600' }}>info@prixducanada.com</a> directly.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: isMobile ? '1rem 1.5rem' : '0.95rem 1.5rem',
                    minHeight: isMobile ? '54px' : 'auto',
                    background: isSubmitting ? '#ccc' : 'var(--primary-gold)',
                    color: 'white', border: 'none', borderRadius: '10px',
                    fontSize: isMobile ? '1.05rem' : '1rem', fontWeight: '700',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s ease, transform 0.1s ease',
                    letterSpacing: '0.5px',
                    fontFamily: 'inherit',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: '0 4px 12px rgba(201, 169, 110, 0.25)'
                  }}
                  onTouchStart={(e) => { if (!isSubmitting) e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  {isSubmitting ? 'Sending…' : `Reserve My Spot${numberOfClasses > 0 ? ` · $${totalCost} + HST` : ''}`}
                </button>
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--soft-gray)', textAlign: 'center', lineHeight: '1.5' }}>
                  Spaces are limited. Payment instructions will be sent in your confirmation email.
                </p>
              </form>
            )}

            {/* Instructors */}
            <div style={{ marginTop: '2rem', paddingTop: '1.75rem', borderTop: '1px solid #eee' }}>
              <p style={{ ...eyebrowStyle, textAlign: 'left', marginBottom: '1.25rem', fontSize: '0.7rem' }}>
                Meet Your Instructors
              </p>

              <div style={{ display: 'grid', gap: '1rem' }}>
                {sessions.map((s) => (
                  <div key={s.key} style={{
                    padding: isMobile ? '1.1rem 1rem' : '1.25rem',
                    borderRadius: '10px', background: '#fafafa',
                    border: '1px solid #eee'
                  }}>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--charcoal)', fontSize: '1.2rem', fontFamily: "'Playfair Display', Georgia, serif", textAlign: 'left' }}>
                      {s.instructor}
                    </h3>
                    <p style={{ margin: 0, color: 'var(--charcoal)', lineHeight: '1.7', fontSize: '0.95rem', textAlign: 'left', whiteSpace: 'pre-line' }}>
                      {bioExpanded[s.key] ? s.fullBio : s.shortBio}
                    </p>
                    {s.fullBio && s.fullBio !== s.shortBio && (
                      <button
                        type="button"
                        onClick={() => setBioExpanded({ ...bioExpanded, [s.key]: !bioExpanded[s.key] })}
                        style={{
                          marginTop: '0.65rem',
                          padding: isMobile ? '0.5rem 0' : '0.35rem 0',
                          minHeight: isMobile ? '40px' : 'auto',
                          background: 'none', border: 'none',
                          color: 'var(--primary-gold)', fontWeight: '600', fontSize: '0.9rem',
                          cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                      >
                        {bioExpanded[s.key] ? 'Show less ↑' : 'Read full bio →'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PreCompetitionClasses;
